/**
 * THE WIRE CONTRACT for the Data Comb findings surface.
 *
 * Mirrors `Lighthouse.Api.Contracts.Etl.DataCombContracts` 1:1. Unlike
 * `types/observatory.ts` — which was written ahead of its endpoints and carried
 * a mock layer behind it — every shape here is served by
 * `DataCombController` today. There is no mock and no `delay()`: the comb writes
 * three real tables and this reads them.
 */

/** The comb's three levels. Deliberately not the four-step `SeverityLevel`. */
export type CombSeverity = 'critical' | 'warning' | 'info'

/** Which slice of a run's findings the list is showing. */
export type FindingState = 'outstanding' | 'acknowledged' | 'all'

/**
 * The rules the comb has, as stable slugs.
 *
 * Not an exhaustive union on the wire — the server sends whatever `RuleId` it
 * stored, and a rule can be added without a migration. These are the seven in
 * `DataCombRules` today, for labelling; an unrecognised id falls back to the raw
 * slug rather than being dropped. Keep this list in step with that class: the
 * earlier-pass rule filter offers exactly these, because the latest pass's
 * counts cannot say which rules fired on another pass.
 */
export const RULE_LABELS: Record<string, string> = {
  'column-always-empty': 'Always empty',
  'column-always-blank': 'Always blank or zero',
  'column-sparsely-populated': 'Sparsely populated',
  'column-fill-rate-dropped': 'Fill rate dropped',
  'landing-residue': 'Landing residue',
  'table-profile-skipped': 'Not profiled',
  'source-key-lossy': 'Lossy source key',
}

/**
 * The longer form, shown in the detail panel.
 *
 * A rule id says what fired; this says what it means, which is what someone
 * seeing a finding for the first time actually needs.
 */
export const RULE_DESCRIPTIONS: Record<string, string> = {
  'column-always-empty':
    'The table has rows and this column is NULL in every one of them. Either the load does not populate it or the column is not needed.',
  'column-always-blank':
    'The column is non-NULL throughout, but every value is an empty string or a zero. A NULL check alone would report it as fully populated.',
  'column-sparsely-populated':
    'The column carries a value in fewer rows than the configured threshold. Not necessarily wrong — worth knowing whether it is intended.',
  'column-fill-rate-dropped':
    'The column was populated and now is less so. This is the only rule that needs no expectation at all: something changed.',
  'landing-residue':
    'A landing table still holds rows. Each transform deletes what it consumed in the same transaction, so anything left is work a run did not finish.',
  'table-profile-skipped':
    'The sweep could not measure this table. Reported rather than passed over silently, because a pass that quietly covered less than it claims is worse than one that failed.',
  'source-key-lossy':
    'The SourceKey is built by concatenating columns with no separator, so two different source rows can produce the same key. Structural: not a measured collision.',
}

/**
 * Every provenance a finding can name, mirroring `Caravan.Abstractions.SourceSystem`.
 *
 * Only the earlier-pass provenance filter uses this. The latest pass's filter is
 * built from the summary's server-side counts, which is the only complete list
 * for THAT pass; for any other pass nothing counts provenances, so the filter
 * offers every value the server's `Enum.TryParse` accepts rather than the latest
 * pass's subset.
 */
export const SOURCE_SYSTEMS: readonly string[] = [
  'Unknown', 'OracleEbs', 'BusinessCentral', 'Terradome', 'Gis', 'ManualEntry', 'Lighthouse',
]

export interface CombRun {
  runId: string
  runKey: string
  /** Running | Succeeded | SucceededWithWarnings | Failed | Cancelled | Halted */
  status: string
  startedAt: string
  completedAt: string | null
  durationMs: number | null
  tablesProfiled: number
  tablesSkipped: number
  columnsProfiled: number
  findingsCritical: number
  findingsWarning: number
  findingsInfo: number
  workerName: string | null
  hangfireJobId: string | null
  /** Why the pass failed. Withheld from EVERY non-administrator when recorded, whatever it says (D8, fail closed). */
  lastError: string | null
  /** True when an error is recorded and this caller may not read it. Absent from an older API: false. */
  lastErrorWithheld?: boolean
}

export interface CombSeverityCount {
  severity: CombSeverity
  count: number
}

export interface CombRuleCount {
  ruleId: string
  severity: CombSeverity
  count: number
}

/**
 * Outstanding findings per provenance.
 *
 * Computed server-side over the whole run, which is the only way a filter list
 * can be complete — derived from a page it would only ever offer the provenances
 * that happen to be on screen. `sourceSystem` is null for the findings that are
 * not about row data at all.
 */
export interface CombSourceCount {
  sourceSystem: string | null
  count: number
}

/**
 * Whether the signed-in caller may acknowledge — the summary's copy of the answer.
 *
 * STILL SERVED, AND NOT READ FOR GATING. Since R3 (2026-10-01) acknowledging and
 * restoring are pinned to an Entra sign-in, and the server computes this from that
 * same pinned decision (the catalogue's `canAcknowledgeCombFindings`, with the same
 * reason text), so it never says yes to the shared API key. The Data Comb page
 * gates on the catalogue's flag instead, as the Scheduler gates its writes
 * (combFormat.ts `acknowledgeGate`); this stays on the wire for a bundle cached
 * across a deploy. Never derived from a token claim either way.
 */
export interface CombPermission {
  canAcknowledge: boolean
  deniedReason: string | null
}

export interface CombSummary {
  /** Null when the comb has never run against this database. */
  latestRun: CombRun | null
  previousRun: CombRun | null
  severities: CombSeverityCount[]
  rules: CombRuleCount[]
  sources: CombSourceCount[]
  outstanding: number
  acknowledged: number
  newSincePreviousRun: number
  permission: CombPermission
}

export interface CombFinding {
  id: string
  runId: string
  ruleId: string
  severity: CombSeverity
  schemaName: string
  tableName: string
  /** Null for a table-level or landing-level finding. */
  columnName: string | null
  /**
   * Which provenance the finding is about; null when it is not about row data.
   *
   * Part of a finding's identity rather than a detail of it. The profiler measures
   * per source system, so one column can be empty for Oracle-loaded rows and
   * populated for hand-entered ones — without this the two arrive as rows that
   * look identical in the table.
   */
  sourceSystem: string | null
  /** Pre-joined `Schema.Table.Column`. */
  target: string
  message: string
  observed: string | null
  expected: string | null
  firstSeenAt: string
  lastSeenAt: string
  isNew: boolean
  ageDays: number
  acknowledgedAt: string | null
  acknowledgedBy: string | null
  acknowledgementNote: string | null
}

export interface CombColumnHistoryPoint {
  runId: string
  runKey: string
  observedAt: string
  /** Unknown | OracleEbs | BusinessCentral | Terradome | Gis | ManualEntry | Lighthouse */
  sourceSystem: string
  rowCount: number
  nonNullCount: number
  blankCount: number
  zeroCount: number
  fillRatePercent: number
}

export interface CombColumnHistory {
  schemaName: string
  tableName: string
  columnName: string
  dataType: string | null
  points: CombColumnHistoryPoint[]
}

export interface FindingsQuery {
  runId?: string
  severity?: CombSeverity
  ruleId?: string
  sourceSystem?: string
  state?: FindingState
  search?: string
  skip?: number
  top?: number
}
